import React, { useState, useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import {
  StopGameRoom,
  RoomPlayer,
  createRoom,
  joinRoom,
  leaveRoom,
  startRound,
  callStop,
  submitAnswers,
  finalizeRoundScores,
  listenToRoom,
  voteOnWord,
  getRoom,
  updateRoomSettings,
  skipRoundLetter,
  checkPendingVoters,
  PendingVoter,
  inviteFriendToStopGame
} from '../../services/stopGameRoomService';
import { getFriends, SocialUser } from '../../services/socialService';
import {
  DEFAULT_STOP_CATEGORIES,
  StopCategory,
  validateWordWithApi,
  WordValidationResult,
  calculateStopScores,
  getCategorySuggestions
} from '../../services/stopGameValidator';
import { playBuzzerSound, playSuccessSound, playTickSound, speakWord } from '../../utils/stopGameAudio';
import StopLetterWheel from './StopLetterWheel';
import StopCategoryInput from './StopCategoryInput';
import { WordPopover } from '../WordPopover';
import {
  Users,
  Copy,
  Check,
  Crown,
  Play,
  Share2,
  Trophy,
  Timer,
  AlertCircle,
  ArrowLeft,
  Volume2,
  CheckCircle2,
  XCircle,
  HelpCircle,
  Sparkles,
  ThumbsUp,
  ThumbsDown,
  ChevronLeft,
  ChevronRight,
  UserCheck,
  UserPlus,
  BookOpen,
  RefreshCw,
  Plus,
  Trash2,
  Settings,
  Tag
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

interface StopGameMultiplayerProps {
  initialRoomCode?: string;
  onBackToMenu: () => void;
}

export const StopGameMultiplayer: React.FC<StopGameMultiplayerProps> = ({
  initialRoomCode,
  onBackToMenu
}) => {
  const { currentUser } = useAuth();

  // Player identity
  const [playerName, setPlayerName] = useState(
    currentUser?.displayName || currentUser?.email?.split('@')[0] || ''
  );
  const [playerId] = useState(
    () => currentUser?.uid || `guest_${Math.random().toString(36).substring(2, 9)}`
  );

  // Room state
  const [roomCodeInput, setRoomCodeInput] = useState(initialRoomCode || '');
  const [currentRoom, setCurrentRoom] = useState<StopGameRoom | null>(null);
  const [isJoining, setIsJoining] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  // Local game state for round
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [timeLeft, setTimeLeft] = useState(60);
  const [isValidatingLocal, setIsValidatingLocal] = useState(false);
  const [isSpinning, setIsSpinning] = useState(false);
  const [isSkippingLetter, setIsSkippingLetter] = useState(false);
  const [activeCategoryIndex, setActiveCategoryIndex] = useState(0);
  const [showPendingVotesPrompt, setShowPendingVotesPrompt] = useState(false);
  const [pendingVotersList, setPendingVotersList] = useState<PendingVoter[]>([]);
  const [popoverWord, setPopoverWord] = useState<string | null>(null);
  const [popoverPosition, setPopoverPosition] = useState<{ top: number; left: number } | null>(null);
  const syncTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Friends invite state
  const [friends, setFriends] = useState<SocialUser[]>([]);
  const [invitedFriendIds, setInvitedFriendIds] = useState<Record<string, boolean>>({});
  const [invitingFriendId, setInvitingFriendId] = useState<string | null>(null);

  useEffect(() => {
    if (currentUser?.uid) {
      const unsub = getFriends(currentUser.uid, setFriends);
      return () => {
        if (typeof unsub === 'function') unsub();
      };
    }
  }, [currentUser?.uid]);

  const handleInviteFriend = async (friend: SocialUser) => {
    if (!currentRoom) return;
    try {
      setInvitingFriendId(friend.id);
      await inviteFriendToStopGame(
        currentRoom.code,
        {
          id: playerId,
          name: playerName.trim() || 'Host',
          avatarUrl: currentUser?.photoURL || undefined
        },
        friend.id,
        friend.username
      );
      setInvitedFriendIds(prev => ({ ...prev, [friend.id]: true }));
    } catch (err) {
      console.error('Failed to invite friend:', err);
    } finally {
      setInvitingFriendId(null);
    }
  };

  const handleWordClick = (e: React.MouseEvent, wordToInspect: string) => {
    e.stopPropagation();
    const clean = wordToInspect.trim();
    if (!clean) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const popoverHeight = 360;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < popoverHeight && rect.top > popoverHeight
      ? Math.max(10, rect.top - popoverHeight)
      : Math.min(window.innerHeight - popoverHeight, rect.bottom + 8);
    const left = Math.max(16, Math.min(window.innerWidth - 300, rect.left));
    setPopoverWord(clean);
    setPopoverPosition({ top, left });
  };

  // Settings for room creation
  const [roundsSetting, setRoundsSetting] = useState(3);
  const [durationSetting, setDurationSetting] = useState(60);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([
    'animal',
    'food',
    'country_city',
    'job',
    'object',
    'verb'
  ]);
  const [showCategoryPickerInCreate, setShowCategoryPickerInCreate] = useState(false);
  const [showAddCategoryInLobby, setShowAddCategoryInLobby] = useState(false);

  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const answersRef = useRef<Record<string, string>>({});
  const currentRoomRef = useRef<StopGameRoom | null>(currentRoom);
  const hasSubmittedRef = useRef(false);
  const hasValidatedRoundRef = useRef<number>(-1);
  const lastPlayingRoundRef = useRef<number>(-1);
  const lastPlayingLetterRef = useRef<string>('');

  useEffect(() => {
    answersRef.current = answers;
  }, [answers]);

  useEffect(() => {
    currentRoomRef.current = currentRoom;
  }, [currentRoom]);

  const isHost = currentRoom?.hostId === playerId;

  // Listen to room updates
  useEffect(() => {
    if (!currentRoom?.code) return;

    const unsubscribe = listenToRoom(currentRoom.code, updatedRoom => {
      if (!updatedRoom) {
        alert('The room has been closed.');
        setCurrentRoom(null);
        return;
      }
      setCurrentRoom(updatedRoom);
    });

    return () => {
      unsubscribe();
    };
  }, [currentRoom?.code]);

  // Handle countdown & auto STOP in 'playing' status
  // Duration time only begins AFTER the letter for the round has been presented
  useEffect(() => {
    if (!currentRoom || currentRoom.status !== 'playing') {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
      if (currentRoom?.status === 'lobby') {
        lastPlayingRoundRef.current = -1;
        setAnswers({});
        answersRef.current = {};
      }
      return;
    }

    if (
      currentRoom.currentRound !== lastPlayingRoundRef.current ||
      currentRoom.currentLetter !== lastPlayingLetterRef.current
    ) {
      const isReconnecting = lastPlayingRoundRef.current === -1;
      lastPlayingRoundRef.current = currentRoom.currentRound;
      lastPlayingLetterRef.current = currentRoom.currentLetter;

      const now = Date.now();
      const startTime = currentRoom.roundStartTime || 0;
      // If reconnecting and letter presentation has already passed, don't force full wheel spin animation
      const shouldSpin = !startTime || now < startTime;
      setIsSpinning(shouldSpin);

      // Restore submitted or draft answers
      const existingSubmittedAnswers = currentRoom.players?.[playerId]?.answers;
      if (existingSubmittedAnswers && Object.keys(existingSubmittedAnswers).length > 0) {
        hasSubmittedRef.current = true;
        setAnswers(existingSubmittedAnswers);
        answersRef.current = existingSubmittedAnswers;
      } else if (isReconnecting) {
        try {
          const sessionKey = `stop_mp_answers_${currentRoom.code}_${currentRoom.currentRound}`;
          const savedSession = sessionStorage.getItem(sessionKey);
          if (savedSession) {
            const parsed = JSON.parse(savedSession);
            setAnswers(parsed);
            answersRef.current = parsed;
          } else {
            setAnswers({});
            answersRef.current = {};
          }
        } catch {
          setAnswers({});
          answersRef.current = {};
        }
        hasSubmittedRef.current = false;
      } else {
        hasSubmittedRef.current = false;
        setAnswers({});
        answersRef.current = {};
      }
    }

    const duration = currentRoom.roundDuration || 60;
    const startTime =
      currentRoom.roundStartTime ||
      (currentRoom.roundEndTime ? currentRoom.roundEndTime - duration * 1000 : Date.now());
    const endTime = currentRoom.roundEndTime || startTime + duration * 1000;

    const updateTimer = () => {
      const now = Date.now();
      // Hold duration at full time while spinning or before presentation completes!
      if (now < startTime || isSpinning) {
        setTimeLeft(duration);
        return;
      }

      const remaining = Math.max(0, Math.ceil((endTime - now) / 1000));
      setTimeLeft(remaining);

      if (remaining <= 5 && remaining > 0) {
        playTickSound();
      }

      if (remaining <= 0) {
        if (timerRef.current) {
          clearInterval(timerRef.current);
          timerRef.current = null;
        }
        handleAutoStop();
      }
    };

    updateTimer();
    timerRef.current = setInterval(updateTimer, 500);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [
    currentRoom?.status,
    currentRoom?.roundEndTime,
    currentRoom?.roundStartTime,
    currentRoom?.currentRound,
    currentRoom?.currentLetter,
    isSpinning
  ]);

  // When room status transitions to 'validating', trigger submission, buzzer & host validation
  useEffect(() => {
    if (!currentRoom || currentRoom.status !== 'validating') return;

    playBuzzerSound();

    // Ensure my answers are submitted if not already done
    if (!hasSubmittedRef.current) {
      hasSubmittedRef.current = true;
      submitAnswers(currentRoom.code, playerId, answersRef.current).catch(err => {
        console.error('Failed to submit answers in validating phase:', err);
      });
    }

    // Host runs validation and finalizes scores once per round
    if (isHost && hasValidatedRoundRef.current !== currentRoom.currentRound) {
      hasValidatedRoundRef.current = currentRoom.currentRound;
      handleRoundValidation();
    }
  }, [currentRoom?.status, currentRoom?.currentRound, isHost]);

  // Ensure everyone starts voting on Category 1 (index 0) when entering round summary
  useEffect(() => {
    if (currentRoom?.status === 'round_summary') {
      setActiveCategoryIndex(0);
    }
  }, [currentRoom?.status, currentRoom?.currentRound]);

  // Confetti on game over
  useEffect(() => {
    if (currentRoom?.status === 'game_over') {
      try {
        confetti({
          particleCount: 120,
          spread: 80,
          origin: { y: 0.6 }
        });
      } catch {
        // ignore
      }
    }
  }, [currentRoom?.status]);

  // Auto join if initialRoomCode was passed and name exists
  useEffect(() => {
    if (initialRoomCode && playerName && !currentRoom) {
      handleJoinRoom(initialRoomCode);
    }
  }, [initialRoomCode]);

  const updateUrlRoomCode = (code?: string) => {
    try {
      if (typeof window === 'undefined') return;
      const url = new URL(window.location.href);
      if (code) {
        url.searchParams.set('room', code);
        url.searchParams.delete('mode');
      } else {
        url.searchParams.delete('room');
      }
      window.history.replaceState(null, '', url.pathname + url.search);
    } catch {
      // ignore
    }
  };

  const handleCreateRoom = async () => {
    if (!playerName.trim()) {
      alert('Please enter your name');
      return;
    }
    if (selectedCategoryIds.length < 2) {
      alert('Please select at least 2 categories for the game.');
      return;
    }
    setIsJoining(true);
    try {
      const code = await createRoom(
        { id: playerId, name: playerName.trim() },
        {
          totalRounds: roundsSetting,
          roundDuration: durationSetting,
          categoryIds: selectedCategoryIds
        }
      );
      const room = await joinRoom(code, { id: playerId, name: playerName.trim() });
      setCurrentRoom(room);
      updateUrlRoomCode(code);
    } catch (err: any) {
      alert(err.message || 'Failed to create room');
    } finally {
      setIsJoining(false);
    }
  };

  const handleToggleCategoryInCreate = (catId: string) => {
    setSelectedCategoryIds(prev => {
      if (prev.includes(catId)) {
        if (prev.length <= 2) {
          alert('You must have at least 2 categories selected.');
          return prev;
        }
        return prev.filter(id => id !== catId);
      } else {
        return [...prev, catId];
      }
    });
  };

  const handleToggleLobbyCategory = async (catId: string) => {
    if (!currentRoom || !isHost) return;
    const currentCats = currentRoom.categoryIds || [];
    let updatedCats: string[];
    if (currentCats.includes(catId)) {
      if (currentCats.length <= 2) {
        alert('You must have at least 2 categories selected to play.');
        return;
      }
      updatedCats = currentCats.filter(id => id !== catId);
    } else {
      updatedCats = [...currentCats, catId];
    }
    try {
      await updateRoomSettings(currentRoom.code, { categoryIds: updatedCats });
    } catch (e: any) {
      console.error('Failed to update categories:', e);
      alert(e.message || 'Failed to update categories');
    }
  };

  const handleUpdateLobbyDuration = async (newDuration: number) => {
    if (!currentRoom || !isHost) return;
    try {
      await updateRoomSettings(currentRoom.code, { roundDuration: newDuration });
    } catch (e: any) {
      console.error('Failed to update duration:', e);
    }
  };

  const handleUpdateLobbyRounds = async (newRounds: number) => {
    if (!currentRoom || !isHost) return;
    try {
      await updateRoomSettings(currentRoom.code, { totalRounds: newRounds });
    } catch (e: any) {
      console.error('Failed to update rounds:', e);
    }
  };

  const handleSkipRoundLetter = async () => {
    if (!currentRoom || !isHost || isSkippingLetter) return;
    setIsSkippingLetter(true);
    try {
      await skipRoundLetter(currentRoom.code);
    } catch (err: any) {
      console.error('Error skipping to another letter:', err);
      alert(err.message || 'Failed to skip letter');
    } finally {
      setIsSkippingLetter(false);
    }
  };

  const handleJoinRoom = async (codeToJoin?: string) => {
    const code = (codeToJoin || roomCodeInput).trim().toUpperCase();
    if (!code) {
      alert('Please enter a room code');
      return;
    }
    if (!playerName.trim()) {
      alert('Please enter your name');
      return;
    }
    setIsJoining(true);
    try {
      const room = await joinRoom(code, { id: playerId, name: playerName.trim() });
      setCurrentRoom(room);
      updateUrlRoomCode(code);
    } catch (err: any) {
      alert(err.message || 'Room not found');
    } finally {
      setIsJoining(false);
    }
  };

  const handleLeaveRoom = async () => {
    if (currentRoom) {
      updateUrlRoomCode(undefined);
      try {
        sessionStorage.removeItem(`stop_mp_answers_${currentRoom.code}_${currentRoom.currentRound}`);
      } catch {
        // ignore
      }
      await leaveRoom(currentRoom.code, playerId);
      setCurrentRoom(null);
    }
  };

  const handleCopyLink = () => {
    if (!currentRoom) return;
    const url = `${window.location.origin}/stop-game?room=${currentRoom.code}`;
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  const handleAnswerChange = (categoryId: string, val: string) => {
    setAnswers(prev => {
      const updated = { ...prev, [categoryId]: val };
      answersRef.current = updated;
      const room = currentRoomRef.current || currentRoom;
      if (room?.code && room.status === 'playing') {
        try {
          sessionStorage.setItem(`stop_mp_answers_${room.code}_${room.currentRound}`, JSON.stringify(updated));
        } catch {
          // ignore
        }
        if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
        syncTimerRef.current = setTimeout(() => {
          submitAnswers(room.code, playerId, updated).catch(err => {
            console.warn('Auto-sync answer error:', err);
          });
        }, 300);
      }
      return updated;
    });
  };

  const handleStartRound = async () => {
    if (!currentRoom || !isHost) return;
    setAnswers({});
    answersRef.current = {};
    hasSubmittedRef.current = false;
    setActiveCategoryIndex(0);
    setShowPendingVotesPrompt(false);
    await startRound(currentRoom.code);
  };

  const handleHostNextRoundClick = () => {
    if (!currentRoom || !isHost) return;
    const missing = checkPendingVoters(currentRoom);
    if (missing.length > 0) {
      setPendingVotersList(missing);
      setShowPendingVotesPrompt(true);
    } else {
      handleStartRound();
    }
  };

  const handleCallStop = async () => {
    const room = currentRoomRef.current || currentRoom;
    if (!room || room.status !== 'playing') return;
    hasSubmittedRef.current = true;
    try {
      await submitAnswers(room.code, playerId, answersRef.current);
      await callStop(room.code, playerId);
    } catch (e) {
      console.error('Failed to call stop:', e);
    }
  };

  const handleAutoStop = async () => {
    const room = currentRoomRef.current || currentRoom;
    if (!room) return;

    if (!hasSubmittedRef.current) {
      hasSubmittedRef.current = true;
      try {
        await submitAnswers(room.code, playerId, answersRef.current);
      } catch (e) {
        console.error('Failed to submit answers on timeout:', e);
      }
    }

    if (room.status === 'playing') {
      try {
        await callStop(room.code, playerId);
      } catch (e) {
        console.error('Failed to call stop on timeout:', e);
      }
    }
  };

  // Perform dictionary validation (Host handles aggregate calculation to avoid race conditions)
  const handleRoundValidation = async () => {
    const roomCode = currentRoomRef.current?.code || currentRoom?.code;
    if (!roomCode) return;
    setIsValidatingLocal(true);

    // If host, wait a brief 1.5s for all client answers to sync, then validate all words via API
    if (isHost) {
      setTimeout(async () => {
        try {
          const liveRoom = (await getRoom(roomCode)) || currentRoomRef.current || currentRoom;
          if (!liveRoom) return;

          const players = liveRoom.players || {};
          const currentLetter = liveRoom.currentLetter;
          const categoryIds = liveRoom.categoryIds || [];

          const validations: Record<string, Record<string, WordValidationResult>> = {};

          for (const pId of Object.keys(players)) {
            validations[pId] = {};
            const pAnswers = (players[pId]?.answers || {}) as Record<string, string>;

            for (const catId of categoryIds) {
              const rawWord = pAnswers[catId] || '';
              const word = rawWord.trim();
              if (word.length > 0) {
                const valResult = await validateWordWithApi(word, currentLetter, catId);
                validations[pId][catId] = valResult;
              } else {
                validations[pId][catId] = { isValid: false, reason: 'No answer submitted' };
              }
            }
          }

          await finalizeRoundScores(liveRoom.code, validations);
          playSuccessSound();
        } catch (e) {
          console.error('Error finalizing scores:', e);
        } finally {
          setIsValidatingLocal(false);
        }
      }, 2000);
    } else {
      setIsValidatingLocal(false);
    }
  };

  // Helper for categories
  // Dynamically compute scores factoring in real-time player votes
  const dynamicScores = React.useMemo(() => {
    if (!currentRoom || currentRoom.status !== 'round_summary' || !currentRoom.validations) {
      return null;
    }
    const pAnswers: Record<string, Record<string, string>> = {};
    for (const p of Object.values(currentRoom.players || {})) {
      pAnswers[p.id] = p.answers || {};
    }
    return calculateStopScores(pAnswers, currentRoom.validations, currentRoom.votes);
  }, [currentRoom]);

  const categories: StopCategory[] = (currentRoom?.categoryIds || []).map(
    id => DEFAULT_STOP_CATEGORIES.find(c => c.id === id) || DEFAULT_STOP_CATEGORIES[0]
  );

  const playersList = Object.values(currentRoom?.players || {});
  // Sort players by totalScore (factoring in any live round vote adjustments)
  const sortedPlayers = [...playersList].sort((a, b) => {
    const aRound = dynamicScores?.[a.id]?.roundScore ?? a.roundScore;
    const bRound = dynamicScores?.[b.id]?.roundScore ?? b.roundScore;
    const aTotal = (a.totalScore - a.roundScore) + aRound;
    const bTotal = (b.totalScore - b.roundScore) + bRound;
    return bTotal - aTotal;
  });

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={() => {
            if (currentRoom) handleLeaveRoom();
            onBackToMenu();
          }}
          className="inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors text-sm font-semibold"
        >
          <ArrowLeft className="w-4 h-4" />
          {currentRoom ? 'Leave Room' : 'Back to Modes'}
        </button>

        {currentRoom && (
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wider">Room Code:</span>
            <span className="font-mono font-black text-lg text-purple-700 bg-purple-100 px-3 py-1 rounded-xl border border-purple-200">
              {currentRoom.code}
            </span>
          </div>
        )}
      </div>

      {/* NOT IN A ROOM: Create or Join Screen */}
      {!currentRoom && (
        <div className="bg-white rounded-3xl p-6 md:p-10 shadow-xl border border-gray-100 max-w-xl mx-auto animate-in fade-in">
          <div className="w-16 h-16 bg-gradient-to-tr from-indigo-600 to-purple-600 rounded-2xl flex items-center justify-center text-white mx-auto mb-4 shadow-lg shadow-indigo-500/30">
            <Users className="w-8 h-8" />
          </div>
          <h2 className="text-3xl font-extrabold text-gray-900 text-center">Online Multiplayer</h2>
          <p className="text-gray-600 mt-2 text-center text-sm">
            Play with your friends anywhere! Create a private game room and share the invite code or link.
          </p>

          <div className="mt-6">
            <label className="block text-xs font-bold text-gray-700 uppercase mb-1">Your Player Name</label>
            <input
              type="text"
              value={playerName}
              onChange={e => setPlayerName(e.target.value)}
              placeholder="Enter your name"
              className="w-full px-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-purple-400 focus:outline-none font-semibold text-gray-900"
            />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-8 pt-6 border-t border-gray-100">
            {/* Create Room */}
            <div className="bg-purple-50/50 p-5 rounded-2xl border border-purple-100 flex flex-col justify-between">
              <div>
                <h3 className="font-extrabold text-gray-900 text-base flex items-center gap-2">
                  <Crown className="w-4 h-4 text-amber-500" />
                  Host a Room
                </h3>
                <p className="text-xs text-gray-500 mt-1 mb-4">
                  Create a new game, pick rules, and invite your friends.
                </p>

                <div className="space-y-3 mb-4 text-xs font-bold text-gray-600">
                  <div className="flex justify-between items-center">
                    <span>Rounds:</span>
                    <select
                      value={roundsSetting}
                      onChange={e => setRoundsSetting(Number(e.target.value))}
                      className="bg-white border rounded-lg px-2 py-1 text-xs font-bold text-gray-800"
                    >
                      <option value={1}>1 Round (Quick)</option>
                      <option value={2}>2 Rounds</option>
                      <option value={3}>3 Rounds (Standard)</option>
                      <option value={5}>5 Rounds (Classic)</option>
                      <option value={7}>7 Rounds (Extended)</option>
                      <option value={10}>10 Rounds (Marathon)</option>
                      <option value={15}>15 Rounds (Epic)</option>
                    </select>
                  </div>
                  <div className="flex justify-between items-center">
                    <span>Round Timer:</span>
                    <select
                      value={durationSetting}
                      onChange={e => setDurationSetting(Number(e.target.value))}
                      className="bg-white border rounded-lg px-2 py-1 text-xs font-bold text-gray-800"
                    >
                      <option value={30}>30 seconds (Speed run)</option>
                      <option value={45}>45 seconds (Fast)</option>
                      <option value={60}>60 seconds (1 min - Standard)</option>
                      <option value={75}>75 seconds (1.25 min)</option>
                      <option value={90}>90 seconds (1.5 min)</option>
                      <option value={120}>120 seconds (2 min - Relaxed)</option>
                      <option value={150}>150 seconds (2.5 min)</option>
                      <option value={180}>180 seconds (3 min - Extended)</option>
                      <option value={240}>240 seconds (4 min - Thoughtful)</option>
                      <option value={300}>300 seconds (5 min - Casual)</option>
                    </select>
                  </div>

                  {/* Categories Selector */}
                  <div className="pt-2 border-t border-purple-100">
                    <div className="flex items-center justify-between mb-1">
                      <span className="flex items-center gap-1 text-gray-700">
                        <Tag className="w-3.5 h-3.5 text-purple-600" />
                        Categories ({selectedCategoryIds.length})
                      </span>
                      <button
                        type="button"
                        onClick={() => setShowCategoryPickerInCreate(!showCategoryPickerInCreate)}
                        className="text-[11px] text-purple-600 hover:text-purple-800 underline font-semibold"
                      >
                        {showCategoryPickerInCreate ? 'Close' : 'Choose'}
                      </button>
                    </div>

                    {showCategoryPickerInCreate ? (
                      <div className="bg-white p-2.5 rounded-xl border border-purple-200 space-y-2 mt-2 shadow-xs">
                        <div className="flex justify-between items-center text-[10px]">
                          <button
                            type="button"
                            onClick={() =>
                              setSelectedCategoryIds([
                                'animal',
                                'food',
                                'country_city',
                                'job',
                                'object',
                                'verb'
                              ])
                            }
                            className="text-purple-600 hover:underline font-bold"
                          >
                            Default (6)
                          </button>
                          <button
                            type="button"
                            onClick={() => setSelectedCategoryIds(DEFAULT_STOP_CATEGORIES.map(c => c.id))}
                            className="text-purple-600 hover:underline font-bold"
                          >
                            Select All ({DEFAULT_STOP_CATEGORIES.length})
                          </button>
                        </div>
                        <div className="grid grid-cols-2 gap-1.5 max-h-48 overflow-y-auto pr-1">
                          {DEFAULT_STOP_CATEGORIES.map(cat => {
                            const isSelected = selectedCategoryIds.includes(cat.id);
                            return (
                              <button
                                key={cat.id}
                                type="button"
                                onClick={() => handleToggleCategoryInCreate(cat.id)}
                                className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-bold transition-all text-left border ${
                                  isSelected
                                    ? 'bg-purple-100/90 border-purple-300 text-purple-900 shadow-2xs'
                                    : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                                }`}
                              >
                                <span className="text-sm">{cat.icon}</span>
                                <span className="truncate flex-1">{cat.name}</span>
                                {isSelected && <Check className="w-3 h-3 text-purple-700 shrink-0" />}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-1 mt-1.5">
                        {selectedCategoryIds.map(id => {
                          const cat = DEFAULT_STOP_CATEGORIES.find(c => c.id === id);
                          if (!cat) return null;
                          return (
                            <span
                              key={id}
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-md text-[10px] font-bold bg-white text-purple-800 border border-purple-200"
                            >
                              <span>{cat.icon}</span>
                              <span className="truncate max-w-[80px]">{cat.name}</span>
                            </span>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <button
                onClick={handleCreateRoom}
                disabled={isJoining || !playerName.trim()}
                className="w-full py-3 bg-purple-600 hover:bg-purple-700 text-white font-extrabold text-sm rounded-xl shadow-md transition-all disabled:opacity-50 mt-4"
              >
                {isJoining ? 'Creating...' : 'Create Room'}
              </button>
            </div>

            {/* Join Room */}
            <div className="bg-gray-50 p-5 rounded-2xl border border-gray-200 flex flex-col justify-between">
              <div>
                <h3 className="font-extrabold text-gray-900 text-base flex items-center gap-2">
                  <Users className="w-4 h-4 text-indigo-500" />
                  Join Room
                </h3>
                <p className="text-xs text-gray-500 mt-1 mb-4">
                  Got an invite code from a friend? Enter it below.
                </p>

                <input
                  type="text"
                  value={roomCodeInput}
                  onChange={e => setRoomCodeInput(e.target.value.toUpperCase())}
                  placeholder="e.g. 5K9WA"
                  maxLength={5}
                  className="w-full px-3 py-2.5 rounded-xl border border-gray-300 focus:ring-2 focus:ring-indigo-400 focus:outline-none uppercase font-mono font-bold text-center tracking-widest text-lg mb-4"
                />
              </div>

              <button
                onClick={() => handleJoinRoom()}
                disabled={isJoining || !playerName.trim() || !roomCodeInput.trim()}
                className="w-full py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-extrabold text-sm rounded-xl shadow-md transition-all disabled:opacity-50"
              >
                {isJoining ? 'Joining...' : 'Join Game'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* LOBBY PHASE */}
      {currentRoom && currentRoom.status === 'lobby' && (
        <div className="bg-white rounded-3xl p-6 md:p-10 shadow-xl border border-gray-100 max-w-2xl mx-auto text-center animate-in fade-in space-y-6">
          <div>
            <span className="text-xs font-black uppercase text-purple-600 bg-purple-100 px-3 py-1 rounded-full">
              Game Lobby
            </span>
            <h2 className="text-3xl font-black text-gray-900 mt-3">Ready to Play?</h2>
            <p className="text-gray-600 text-sm mt-1">
              Share this room code with your friends so they can join you online.
            </p>
          </div>

          {/* Room Code & Share */}
          <div className="p-4 bg-gradient-to-r from-purple-50 to-indigo-50 rounded-2xl border border-purple-200 flex flex-col sm:flex-row items-center justify-between gap-3">
            <div>
              <span className="text-xs font-bold text-gray-500 uppercase">Room Code</span>
              <div className="text-4xl font-mono font-black text-purple-900 tracking-wider">
                {currentRoom.code}
              </div>
            </div>

            <button
              onClick={handleCopyLink}
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-white hover:bg-purple-50 text-purple-700 border border-purple-300 rounded-xl font-bold text-sm shadow-sm transition-all"
            >
              {copiedLink ? <Check className="w-4 h-4 text-emerald-600" /> : <Copy className="w-4 h-4" />}
              {copiedLink ? 'Copied Link!' : 'Copy Invite Link'}
            </button>
          </div>

          {/* Invite Friends Panel */}
          {friends.length > 0 && (
            <div className="text-left p-4 bg-gradient-to-r from-purple-50/70 to-pink-50/70 rounded-2xl border border-purple-100 space-y-3">
              <div className="flex items-center justify-between">
                <h4 className="text-xs font-bold uppercase text-purple-900 tracking-wider flex items-center gap-1.5">
                  <UserPlus className="w-4 h-4 text-purple-600" />
                  Invite Friends to Room
                </h4>
                <span className="text-[11px] text-purple-600 font-semibold">
                  Sends push notification
                </span>
              </div>
              <div className="flex gap-2 overflow-x-auto pb-1 pt-0.5">
                {friends.map(friend => {
                  const isAlreadyInRoom = Boolean(currentRoom.players && currentRoom.players[friend.id]);
                  const hasInvited = invitedFriendIds[friend.id];
                  const isInviting = invitingFriendId === friend.id;

                  return (
                    <div
                      key={friend.id}
                      className="flex items-center gap-2 p-2 bg-white rounded-xl border border-purple-100 shadow-2xs shrink-0"
                    >
                      {friend.profilePhotoUrl ? (
                        <img
                          src={friend.profilePhotoUrl}
                          alt=""
                          className="w-7 h-7 rounded-full object-cover border border-purple-200"
                        />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-indigo-600 text-white font-bold flex items-center justify-center text-xs">
                          {friend.username?.charAt(0).toUpperCase() || 'F'}
                        </div>
                      )}
                      <span className="text-xs font-bold text-gray-800 max-w-[100px] truncate">
                        {friend.username}
                      </span>
                      {isAlreadyInRoom ? (
                        <span className="text-[10px] font-bold text-emerald-600 bg-emerald-50 px-2 py-1 rounded-lg">
                          Joined
                        </span>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleInviteFriend(friend)}
                          disabled={hasInvited || isInviting}
                          className="text-[11px] font-bold px-2.5 py-1 rounded-lg transition-all flex items-center gap-1 bg-purple-600 hover:bg-purple-700 text-white disabled:bg-emerald-100 disabled:text-emerald-700"
                        >
                          {hasInvited ? (
                            <>
                              <Check className="w-3 h-3 text-emerald-700" />
                              <span>Invited!</span>
                            </>
                          ) : isInviting ? (
                            <RefreshCw className="w-3 h-3 animate-spin" />
                          ) : (
                            <span>Invite</span>
                          )}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Room Settings & Categories Panel */}
          <div className="text-left p-5 bg-purple-50/40 rounded-2xl border border-purple-100 space-y-4">
            <div className="flex items-center justify-between">
              <h4 className="text-xs font-bold uppercase text-purple-900 tracking-wider flex items-center gap-1.5">
                <Settings className="w-4 h-4 text-purple-600" />
                Game Rules & Categories
              </h4>
              {isHost && (
                <span className="text-[10px] text-purple-600 font-semibold bg-purple-100 px-2 py-0.5 rounded-full">
                  Host Controls
                </span>
              )}
            </div>

            {/* Duration and Rounds */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              <div className="p-3 bg-white rounded-xl border border-purple-100 flex items-center justify-between">
                <span className="font-bold text-gray-700 flex items-center gap-1">
                  <Timer className="w-3.5 h-3.5 text-purple-600" />
                  Round Timer:
                </span>
                {isHost ? (
                  <select
                    value={currentRoom.roundDuration || 60}
                    onChange={e => handleUpdateLobbyDuration(Number(e.target.value))}
                    className="bg-purple-50 font-bold text-purple-900 border border-purple-200 rounded-lg px-2 py-1 text-xs"
                  >
                    <option value={30}>30s (Speed)</option>
                    <option value={45}>45s (Fast)</option>
                    <option value={60}>60s (1 min)</option>
                    <option value={75}>75s (1.25 min)</option>
                    <option value={90}>90s (1.5 min)</option>
                    <option value={120}>120s (2 min)</option>
                    <option value={150}>150s (2.5 min)</option>
                    <option value={180}>180s (3 min)</option>
                    <option value={240}>240s (4 min)</option>
                    <option value={300}>300s (5 min)</option>
                  </select>
                ) : (
                  <span className="font-extrabold text-purple-900">
                    {currentRoom.roundDuration || 60} seconds
                  </span>
                )}
              </div>

              <div className="p-3 bg-white rounded-xl border border-purple-100 flex items-center justify-between">
                <span className="font-bold text-gray-700 flex items-center gap-1">
                  <Trophy className="w-3.5 h-3.5 text-amber-500" />
                  Total Rounds:
                </span>
                {isHost ? (
                  <select
                    value={currentRoom.totalRounds || 3}
                    onChange={e => handleUpdateLobbyRounds(Number(e.target.value))}
                    className="bg-purple-50 font-bold text-purple-900 border border-purple-200 rounded-lg px-2 py-1 text-xs"
                  >
                    <option value={1}>1 Round</option>
                    <option value={2}>2 Rounds</option>
                    <option value={3}>3 Rounds</option>
                    <option value={5}>5 Rounds</option>
                    <option value={7}>7 Rounds</option>
                    <option value={10}>10 Rounds</option>
                    <option value={15}>15 Rounds</option>
                  </select>
                ) : (
                  <span className="font-extrabold text-purple-900">
                    {currentRoom.totalRounds || 3} Rounds
                  </span>
                )}
              </div>
            </div>

            {/* Categories in Lobby */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs font-bold text-gray-700 flex items-center gap-1">
                  <Tag className="w-3.5 h-3.5 text-purple-600" />
                  Active Categories ({(currentRoom.categoryIds || []).length}):
                </span>
                {isHost && (
                  <button
                    type="button"
                    onClick={() => setShowAddCategoryInLobby(!showAddCategoryInLobby)}
                    className="inline-flex items-center gap-1 text-[11px] font-bold text-purple-700 hover:text-purple-900 bg-white border border-purple-200 px-2 py-0.5 rounded-lg shadow-2xs transition-all"
                  >
                    <Plus className="w-3 h-3" />
                    {showAddCategoryInLobby ? 'Hide Catalog' : 'Add / Remove Categories'}
                  </button>
                )}
              </div>

              {/* Category Badges */}
              <div className="flex flex-wrap gap-1.5">
                {(currentRoom.categoryIds || []).map(id => {
                  const cat = DEFAULT_STOP_CATEGORIES.find(c => c.id === id);
                  if (!cat) return null;
                  return (
                    <span
                      key={id}
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-xl text-xs font-bold bg-white text-purple-900 border border-purple-200 shadow-2xs"
                    >
                      <span className="text-sm">{cat.icon}</span>
                      <span>{cat.name}</span>
                      {isHost && (
                        <button
                          type="button"
                          onClick={() => handleToggleLobbyCategory(cat.id)}
                          className="text-gray-400 hover:text-red-600 ml-0.5 transition-colors"
                          title="Remove category"
                        >
                          <XCircle className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </span>
                  );
                })}
              </div>

              {/* Add/Remove Category Drawer for Host */}
              {isHost && showAddCategoryInLobby && (
                <div className="mt-3 p-3 bg-white rounded-xl border border-purple-200 space-y-2 animate-in fade-in">
                  <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide">
                    Click to add or remove category:
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 max-h-48 overflow-y-auto pr-1">
                    {DEFAULT_STOP_CATEGORIES.map(cat => {
                      const isSelected = (currentRoom.categoryIds || []).includes(cat.id);
                      return (
                        <button
                          key={cat.id}
                          type="button"
                          onClick={() => handleToggleLobbyCategory(cat.id)}
                          className={`flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-bold transition-all text-left border ${
                            isSelected
                              ? 'bg-purple-100/90 border-purple-300 text-purple-900 shadow-2xs'
                              : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                          }`}
                        >
                          <span className="text-sm">{cat.icon}</span>
                          <span className="truncate flex-1">{cat.name}</span>
                          {isSelected ? (
                            <Check className="w-3 h-3 text-purple-700 shrink-0" />
                          ) : (
                            <Plus className="w-3 h-3 text-gray-400 shrink-0" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Connected Players */}
          <div className="text-left">
            <h4 className="text-xs font-bold uppercase text-gray-500 mb-3 tracking-wider">
              Connected Players ({playersList.length})
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {playersList.map(player => (
                <div
                  key={player.id}
                  className="flex items-center justify-between p-3.5 bg-gray-50 rounded-2xl border border-gray-200"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-purple-500 to-indigo-600 text-white font-bold flex items-center justify-center text-sm shadow-sm">
                      {player.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-bold text-gray-900 text-sm flex items-center gap-1.5">
                        {player.name}
                        {player.isHost && (
                          <Crown className="w-3.5 h-3.5 text-amber-500" title="Host" />
                        )}
                        {player.id === playerId && (
                          <span className="text-[10px] text-gray-400 font-normal">(You)</span>
                        )}
                      </div>
                      <span className="text-xs text-emerald-600 font-medium">Ready</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Start Action */}
          {isHost ? (
            <div className="pt-2">
              <button
                onClick={handleStartRound}
                className="w-full sm:w-auto px-12 py-4 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-black text-lg rounded-2xl shadow-xl shadow-purple-500/25 transition-all transform hover:scale-105"
              >
                Start Game!
              </button>
            </div>
          ) : (
            <div className="p-4 bg-amber-50 rounded-2xl border border-amber-200 text-amber-800 text-sm font-semibold flex items-center justify-center gap-2">
              <Timer className="w-4 h-4 animate-spin" />
              Waiting for the host ({currentRoom.players[currentRoom.hostId]?.name || 'Host'}) to start...
            </div>
          )}
        </div>
      )}

      {/* PLAYING PHASE */}
      {currentRoom && currentRoom.status === 'playing' && (
        <div className="space-y-6">
          <div className="bg-white rounded-3xl p-6 shadow-md border border-gray-100 flex flex-col items-center">
            <div className="w-full flex items-center justify-between mb-2">
              <span className="text-xs font-bold text-gray-500 uppercase">
                Round {currentRoom.currentRound} of {currentRoom.totalRounds}
              </span>
              <div className="flex items-center gap-1 text-sm font-bold text-purple-700">
                <Users className="w-4 h-4" />
                <span>{playersList.length} Playing</span>
              </div>
            </div>

            <StopLetterWheel
              letter={currentRoom.currentLetter}
              isSpinning={isSpinning}
              onSpinComplete={() => setIsSpinning(false)}
              canReroll={isHost && !isSpinning}
              onReroll={handleSkipRoundLetter}
            />

            {/* Host Skip Round / Reroll Letter Option */}
            {isHost && (
              <div className="mt-2 mb-1">
                <button
                  type="button"
                  onClick={handleSkipRoundLetter}
                  disabled={isSkippingLetter}
                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 rounded-xl text-xs font-bold transition-all transform hover:scale-105 active:scale-95 disabled:opacity-50 shadow-2xs"
                  title="Skip this round and roll a new letter for everyone"
                >
                  <RefreshCw className={`w-3.5 h-3.5 ${isSkippingLetter ? 'animate-spin' : ''}`} />
                  <span>{isSkippingLetter ? 'Rolling new letter...' : 'Skip to Another Letter 🎲'}</span>
                </button>
              </div>
            )}

            <div className="w-full max-w-md mt-2">
              <div className="flex items-center justify-between text-sm font-bold mb-1">
                <span className="flex items-center gap-1 text-gray-600">
                  <Timer className="w-4 h-4" />
                  Time Left
                </span>
                <span
                  className={`font-mono text-base ${
                    timeLeft <= 10 ? 'text-red-600 animate-pulse font-black' : 'text-gray-900'
                  }`}
                >
                  {timeLeft}s
                </span>
              </div>
              <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className={`h-full transition-all duration-1000 ${
                    timeLeft <= 10 ? 'bg-red-500' : 'bg-gradient-to-r from-purple-500 to-indigo-500'
                  }`}
                  style={{ width: `${(timeLeft / (currentRoom.roundDuration || 60)) * 100}%` }}
                />
              </div>
            </div>
          </div>

          {/* Spinning Reel Notice or Inputs */}
          {isSpinning && (
            <div className="p-4 bg-gradient-to-r from-purple-100 via-pink-100 to-indigo-100 text-purple-900 font-extrabold text-sm rounded-2xl text-center border border-purple-200 animate-pulse shadow-sm">
              🎲 Choosing Letter... Round duration starts as soon as the letter is revealed!
            </div>
          )}

          {/* Inputs */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {categories.map((cat, idx) => (
              <StopCategoryInput
                key={cat.id}
                category={cat}
                letter={currentRoom.currentLetter}
                value={answers[cat.id] || ''}
                onChange={val => handleAnswerChange(cat.id, val)}
                autoFocus={idx === 0 && !isSpinning}
                disabled={isSpinning}
              />
            ))}
          </div>

          {/* STOP Button */}
          <div className="text-center pt-4">
            <button
              onClick={handleCallStop}
              disabled={isSpinning}
              className={`w-full md:w-auto px-16 py-4 bg-gradient-to-r from-red-600 via-rose-600 to-pink-600 hover:from-red-700 hover:to-pink-700 text-white font-black text-2xl tracking-wider uppercase rounded-2xl shadow-xl shadow-red-500/30 transition-all transform hover:scale-105 active:scale-95 border-2 border-white/20 ${
                isSpinning ? 'opacity-50 cursor-not-allowed transform-none' : ''
              }`}
            >
              🛑 STOP!
            </button>
            <p className="text-xs text-gray-500 mt-2 font-medium">
              First to click STOP ends the round for all players!
            </p>
          </div>
        </div>
      )}

      {/* VALIDATING PHASE */}
      {currentRoom && currentRoom.status === 'validating' && (
        <div className="bg-white rounded-3xl p-12 text-center shadow-xl border border-gray-100 max-w-lg mx-auto">
          <div className="w-16 h-16 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin mx-auto mb-6" />
          <h3 className="text-2xl font-black text-gray-900">🛑 STOP CALLED!</h3>
          <p className="text-gray-600 mt-2 text-sm">
            {currentRoom.stoppedByPlayerId
              ? `${currentRoom.players[currentRoom.stoppedByPlayerId]?.name || 'Someone'} called STOP!`
              : 'Time has expired!'}
          </p>
          <p className="text-purple-600 font-bold text-xs mt-3">
            Validating words with the English Dictionary API & scoring duplicates vs unique words...
          </p>
        </div>
      )}

      {/* ROUND SUMMARY PHASE */}
      {currentRoom && currentRoom.status === 'round_summary' && (
        <div className="bg-white rounded-3xl p-6 md:p-8 shadow-xl border border-gray-100 space-y-8 animate-in fade-in">
          <div className="text-center">
            <span className="text-xs font-black uppercase text-purple-700 bg-purple-100 px-3 py-1 rounded-full">
              Round {currentRoom.currentRound} Results • Letter {currentRoom.currentLetter}
            </span>
            <h2 className="text-3xl font-black text-gray-900 mt-2">Compare Words & Score</h2>
            <p className="text-gray-500 text-sm max-w-md mx-auto mt-1">
              Valid unique words = <strong>10 pts</strong> • Valid duplicate words with a friend = <strong>5 pts</strong>
            </p>
          </div>

          {/* Current Leaderboard Standings */}
          <div className="p-4 bg-purple-50/60 rounded-2xl border border-purple-100">
            <h4 className="text-xs font-bold text-purple-900 uppercase tracking-wider mb-2">
              Leaderboard Standings
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              {sortedPlayers.map((p, idx) => {
                const pRound = dynamicScores?.[p.id]?.roundScore ?? p.roundScore;
                const pTotal = (p.totalScore - p.roundScore) + pRound;
                return (
                  <div key={p.id} className="p-2.5 bg-white rounded-xl border border-purple-100 shadow-sm text-center">
                    <div className="text-xs text-gray-500 font-semibold truncate">
                      #{idx + 1} {p.name}
                    </div>
                    <div className="text-lg font-black text-purple-800">{pTotal} pts</div>
                    <div className="text-[10px] text-emerald-600 font-bold">+{pRound} this round</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Category Stepper Tabs / Progress */}
          {(() => {
            const safeCatIdx = Math.min(Math.max(0, activeCategoryIndex), Math.max(0, categories.length - 1));
            const activeCat = categories[safeCatIdx] || categories[0];
            const playersWithWords = playersList.filter(p => (p.answers?.[activeCat?.id] || '').trim().length > 0);
            const pendingVoters = checkPendingVoters(currentRoom);
            const myPendingCount = pendingVoters.find(p => p.playerId === playerId)?.pendingCount || 0;

            return (
              <div className="space-y-6">
                {/* Category Stepper Pills */}
                <div className="p-3 bg-purple-50/70 rounded-2xl border border-purple-200">
                  <div className="text-[11px] font-bold text-gray-500 uppercase tracking-wide mb-2 flex items-center justify-between">
                    <span>Vote by Category (Same Sequence for Everyone):</span>
                    {myPendingCount === 0 ? (
                      <span className="text-emerald-700 font-extrabold flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        You have voted on all words!
                      </span>
                    ) : (
                      <span className="text-purple-700 font-bold">
                        {myPendingCount} word{myPendingCount > 1 ? 's' : ''} left for you to vote
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-1.5 overflow-x-auto pb-1 scrollbar-none">
                    {categories.map((cat, idx) => {
                      const isActive = idx === safeCatIdx;
                      // Has current user finished all votes in this specific category?
                      const hasVotedAllInCat = !playersList.some(p => {
                        if (p.id === playerId) return false;
                        const w = (p.answers?.[cat.id] || '').trim();
                        if (w.length === 0) return false;
                        return currentRoom.votes?.[p.id]?.[cat.id]?.[playerId] === undefined;
                      });

                      return (
                        <button
                          key={cat.id}
                          onClick={() => setActiveCategoryIndex(idx)}
                          className={`px-3.5 py-1.5 rounded-xl text-xs font-bold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                            isActive
                              ? 'bg-purple-600 text-white shadow-sm ring-2 ring-purple-400'
                              : 'bg-white text-gray-700 hover:bg-purple-50 border border-purple-100'
                          }`}
                        >
                          <span className="text-sm">{cat.icon}</span>
                          <span>{cat.name}</span>
                          {hasVotedAllInCat && (
                            <Check className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-emerald-600'}`} />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Active Category Header Card */}
                {activeCat && (
                  <div className="p-5 bg-gradient-to-r from-purple-50 via-indigo-50 to-purple-50 rounded-2xl border border-purple-200 flex flex-col sm:flex-row sm:items-center justify-between gap-3 shadow-xs">
                    <div className="flex items-center gap-3.5">
                      <div className="w-12 h-12 rounded-2xl bg-white border border-purple-200 flex items-center justify-center text-2xl shadow-sm">
                        {activeCat.icon}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="text-xs font-black uppercase text-purple-700 bg-purple-100 px-2.5 py-0.5 rounded-full">
                            Category {safeCatIdx + 1} of {categories.length}
                          </span>
                          <span className="text-xs font-bold text-gray-500">
                            Letter &ldquo;{currentRoom.currentLetter.toUpperCase()}&rdquo;
                          </span>
                        </div>
                        <h3 className="font-black text-xl text-gray-900 mt-0.5">
                          {activeCat.name}
                        </h3>
                        <p className="text-xs text-gray-600 mt-0.5">
                          {activeCat.description}
                        </p>
                      </div>
                    </div>

                    <div className="text-right sm:text-right flex sm:flex-col items-center sm:items-end justify-between sm:justify-center">
                      <span className="text-[11px] font-bold uppercase text-gray-500">Answers Submitted</span>
                      <span className="text-xl font-black text-purple-900">
                        {playersWithWords.length} / {playersList.length} Players
                      </span>
                    </div>
                  </div>
                )}

                {/* Non-Empty Words Cards for Active Category */}
                {playersWithWords.length === 0 ? (
                  <div className="p-10 text-center bg-gray-50/80 rounded-3xl border border-dashed border-gray-200">
                    <span className="text-4xl mb-2 block">{activeCat?.icon || '📝'}</span>
                    <h4 className="font-extrabold text-gray-800 text-lg">No words submitted for {activeCat?.name}</h4>
                    <p className="text-xs text-gray-500 mt-1 max-w-sm mx-auto">
                      All players left this category blank or did not submit an answer. No votes required!
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {playersWithWords.map(player => {
                      const isMe = player.id === playerId;
                      const rawWord = player.answers?.[activeCat.id] || '';
                      const word = rawWord.trim();
                      const validation = currentRoom.validations?.[player.id]?.[activeCat.id] || { isValid: false };
                      const score = dynamicScores?.[player.id]?.categoryScores?.[activeCat.id] ?? (player.categoryScores?.[activeCat.id] || 0);
                      const voteStatus = dynamicScores?.[player.id]?.categoryVoteStatus?.[activeCat.id] || 'none';

                      const catVotes = currentRoom.votes?.[player.id]?.[activeCat.id] || {};
                      const voteEntries = Object.entries(catVotes);
                      const acceptCount = voteEntries.filter(([vId, v]) => vId !== player.id && v === true).length;
                      const rejectCount = voteEntries.filter(([vId, v]) => vId !== player.id && v === false).length;
                      const myVote = currentRoom.votes?.[player.id]?.[activeCat.id]?.[playerId];

                      return (
                        <div
                          key={player.id}
                          className={`p-4 rounded-2xl border transition-all flex flex-col justify-between ${
                            voteStatus === 'rejected'
                              ? 'bg-red-50/70 border-red-300'
                              : voteStatus === 'disputed'
                                ? 'bg-amber-50/70 border-amber-300'
                                : score > 0
                                  ? score >= 10
                                    ? 'bg-emerald-50/60 border-emerald-300'
                                    : 'bg-amber-50/60 border-amber-300'
                                  : 'bg-red-50/40 border-red-200'
                          }`}
                        >
                          <div>
                            {/* Author Name Header */}
                            <div className="flex items-center justify-between pb-2 mb-2 border-b border-gray-200/70">
                              <div className="flex items-center gap-2">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-tr from-purple-600 to-indigo-600 text-white font-black text-xs flex items-center justify-center shadow-xs">
                                  {player.name.charAt(0).toUpperCase()}
                                </div>
                                <div className="flex items-center gap-1.5">
                                  <span className="font-extrabold text-sm text-gray-900">
                                    {isMe ? 'You' : player.name}
                                  </span>
                                  {isMe && (
                                    <span className="text-[10px] font-bold bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                                      Your Word
                                    </span>
                                  )}
                                </div>
                              </div>

                              {/* Score Badge */}
                              <span
                                className={`px-2.5 py-0.5 rounded-full font-bold text-xs ${
                                  voteStatus === 'rejected'
                                    ? 'bg-red-200 text-red-800'
                                    : voteStatus === 'disputed'
                                      ? 'bg-amber-200 text-amber-800'
                                      : score > 0
                                        ? score >= 10
                                          ? 'bg-emerald-200 text-emerald-800'
                                          : 'bg-amber-200 text-amber-800'
                                        : 'bg-red-200 text-red-800'
                                }`}
                              >
                                +{score} pts
                              </span>
                            </div>

                            {/* Word & Pronunciation */}
                            <div className="flex items-center justify-between mt-1">
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={(e) => handleWordClick(e, validation.correctedWord || word)}
                                  className="font-black text-xl text-gray-900 tracking-tight hover:text-purple-700 underline decoration-dotted decoration-purple-300 underline-offset-4 text-left cursor-pointer transition-colors"
                                  title="Click to inspect word details and translation"
                                >
                                  {word}
                                </button>
                                <button
                                  type="button"
                                  onClick={(e) => handleWordClick(e, validation.correctedWord || word)}
                                  className="p-1 text-purple-600 hover:text-purple-800 hover:bg-purple-50 rounded-md transition-colors cursor-pointer"
                                  title="View dictionary definition"
                                >
                                  <BookOpen className="w-3.5 h-3.5" />
                                </button>
                              </div>
                              {word && (validation.isValid || voteStatus === 'accepted') && (
                                <button
                                  onClick={() => speakWord(validation.correctedWord || word)}
                                  className="p-1.5 text-purple-700 hover:text-purple-900 bg-white rounded-lg border border-purple-100 transition-colors shadow-2xs"
                                  title="Hear pronunciation"
                                >
                                  <Volume2 className="w-4 h-4" />
                                </button>
                              )}
                            </div>

                            {/* Typo Banner */}
                            {validation.isTypo && validation.correctedWord && voteStatus !== 'rejected' && (
                              <div className="text-xs bg-amber-100 text-amber-900 font-bold p-2.5 rounded-xl mt-2 flex items-center justify-between">
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <AlertCircle className="w-4 h-4 text-amber-700 shrink-0" />
                                  <span>
                                    ⚠️ Potential spelling error: Did you mean{' '}
                                    <button
                                      type="button"
                                      onClick={(e) => handleWordClick(e, validation.correctedWord!)}
                                      className="underline font-black text-amber-950 hover:text-amber-800 cursor-pointer"
                                      title="Click to view definition"
                                    >
                                      &ldquo;{validation.correctedWord}&rdquo;
                                    </button>
                                    ? (+{score} pts)
                                  </span>
                                </div>
                                <button
                                  onClick={() => speakWord(validation.correctedWord!)}
                                  className="p-1 text-amber-900 hover:bg-amber-200 rounded-lg ml-2 shrink-0"
                                  title={`Hear: ${validation.correctedWord}`}
                                >
                                  <Volume2 className="w-3.5 h-3.5" />
                                </button>
                              </div>
                            )}

                            {/* Vote Status Badges */}
                            {voteStatus === 'rejected' && (
                              <div className="text-xs bg-red-100 text-red-800 font-bold p-2 rounded-xl mt-2 flex items-center gap-1.5">
                                <XCircle className="w-4 h-4 text-red-600 shrink-0" />
                                <span>Everyone disagreed (0 pts)</span>
                              </div>
                            )}

                            {voteStatus === 'disputed' && (
                              <div className="text-xs bg-amber-100 text-amber-800 font-bold p-2 rounded-xl mt-2 flex items-center gap-1.5">
                                <AlertCircle className="w-4 h-4 text-amber-600 shrink-0" />
                                <span>Most players disagreed (+{score} pts, half points)</span>
                              </div>
                            )}

                            {voteStatus === 'accepted' && !validation.isValid && (
                              <div className="text-xs bg-emerald-100 text-emerald-800 font-bold p-2 rounded-xl mt-2 flex items-center gap-1.5">
                                <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                                <span>Accepted by player vote (+{score} pts)</span>
                              </div>
                            )}

                            {/* Definition & Meaning */}
                            {validation.definition && (
                              <div className="text-xs text-gray-700 bg-white/80 p-2.5 rounded-xl border border-gray-100 mt-2 flex items-start gap-1.5">
                                <BookOpen className="w-3.5 h-3.5 text-purple-600 shrink-0 mt-0.5" />
                                <div>
                                  {validation.partOfSpeech && (
                                    <span className="font-semibold italic text-purple-800 mr-1">
                                      [{validation.partOfSpeech}]
                                    </span>
                                  )}
                                  <span>{validation.definition}</span>
                                </div>
                              </div>
                            )}

                            {/* Invalid dictionary reason if applicable */}
                            {!validation.isValid && word && voteStatus !== 'accepted' && (
                              <div className="text-xs text-red-700 font-semibold bg-red-100/60 p-2 rounded-xl mt-2 border border-red-200/60">
                                {validation.reason || 'Word could not be verified in dictionary'}
                              </div>
                            )}
                          </div>

                          {/* Peer Voting Controls */}
                          <div className="mt-3.5 pt-2.5 border-t border-gray-200/80">
                            {!isMe ? (
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-[11px] font-bold text-gray-500 uppercase tracking-tight">
                                  Vote on {player.name}&rsquo;s word:
                                </span>
                                <div className="flex items-center gap-1.5">
                                  <button
                                    onClick={() => voteOnWord(currentRoom.code, playerId, player.id, activeCat.id, myVote === true ? null : true)}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all ${
                                      myVote === true
                                        ? 'bg-emerald-600 text-white shadow-xs'
                                        : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100 border border-emerald-200'
                                    }`}
                                    title="Vote Agree / Valid"
                                  >
                                    <ThumbsUp className="w-3.5 h-3.5" />
                                    <span>Agree {acceptCount > 0 ? `(${acceptCount})` : ''}</span>
                                  </button>
                                  <button
                                    onClick={() => voteOnWord(currentRoom.code, playerId, player.id, activeCat.id, myVote === false ? null : false)}
                                    className={`px-3 py-1.5 rounded-xl text-xs font-bold flex items-center gap-1.5 transition-all ${
                                      myVote === false
                                        ? 'bg-red-600 text-white shadow-xs'
                                        : 'bg-red-50 text-red-700 hover:bg-red-100 border border-red-200'
                                    }`}
                                    title="Vote Disagree / Invalid"
                                  >
                                    <ThumbsDown className="w-3.5 h-3.5" />
                                    <span>Disagree {rejectCount > 0 ? `(${rejectCount})` : ''}</span>
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="w-full flex items-center justify-between text-xs text-gray-500 font-semibold">
                                <span>🛡️ Room votes on your word:</span>
                                <div className="flex items-center gap-3 font-bold">
                                  <span className="text-emerald-700 flex items-center gap-1">
                                    <ThumbsUp className="w-3.5 h-3.5" /> {acceptCount} Agree
                                  </span>
                                  <span className="text-red-700 flex items-center gap-1">
                                    <ThumbsDown className="w-3.5 h-3.5" /> {rejectCount} Disagree
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Other Words Options People Could Use */}
                {activeCat && (
                  <div className="p-4 bg-purple-50/70 rounded-2xl border border-purple-200">
                    <div className="font-extrabold text-xs text-purple-900 mb-2.5 flex items-center justify-between">
                      <span className="flex items-center gap-1.5">
                        <Sparkles className="w-4 h-4 text-purple-600" />
                        Other words you could have used for &ldquo;{activeCat.name}&rdquo; ({currentRoom.currentLetter.toUpperCase()}):
                      </span>
                      <span className="text-[11px] text-purple-600 font-medium hidden sm:inline">
                        Click any word to inspect definition & translation
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {getCategorySuggestions(currentRoom.currentLetter, activeCat.id).map((suggestionWord: string) => (
                        <button
                          key={suggestionWord}
                          type="button"
                          onClick={(e) => handleWordClick(e, suggestionWord)}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white hover:bg-purple-100 text-purple-900 font-bold text-xs rounded-xl border border-purple-200 transition-all shadow-2xs hover:shadow-xs cursor-pointer group"
                          title={`Click to view definition of "${suggestionWord}"`}
                        >
                          <span>{suggestionWord}</span>
                          <BookOpen className="w-3 h-3 text-purple-500 group-hover:text-purple-700" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Sequential Category Step Navigation */}
                <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                  <button
                    onClick={() => setActiveCategoryIndex(i => Math.max(0, i - 1))}
                    disabled={safeCatIdx <= 0}
                    className="px-4 py-2 rounded-xl text-xs font-bold border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 flex items-center gap-1.5 disabled:opacity-40 disabled:cursor-not-allowed transition-all"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    <span>Previous Category</span>
                  </button>

                  <span className="text-xs font-bold text-gray-500">
                    Category {safeCatIdx + 1} of {categories.length}
                  </span>

                  {safeCatIdx < categories.length - 1 ? (
                    <button
                      onClick={() => setActiveCategoryIndex(i => Math.min(categories.length - 1, i + 1))}
                      className="px-5 py-2 rounded-xl text-xs font-bold bg-purple-600 hover:bg-purple-700 text-white flex items-center gap-1.5 shadow-sm transition-all transform hover:scale-105"
                    >
                      <span>Next Category</span>
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  ) : (
                    <span className="inline-flex items-center gap-1.5 text-xs font-extrabold text-emerald-700 bg-emerald-100 px-3.5 py-1.5 rounded-xl">
                      <Check className="w-3.5 h-3.5" />
                      All Categories Reviewed!
                    </span>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Host Next Round Button & Status Bar */}
          <div className="pt-2 text-center">
            {isHost ? (
              <div>
                <button
                  onClick={handleHostNextRoundClick}
                  className="px-10 py-3.5 bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-extrabold rounded-2xl shadow-xl shadow-purple-500/25 transition-all transform hover:scale-105"
                >
                  {currentRoom.currentRound >= currentRoom.totalRounds ? 'See Final Podium 🏆' : 'Next Round ➡️'}
                </button>
                {(() => {
                  const pending = checkPendingVoters(currentRoom);
                  if (pending.length === 0) {
                    return (
                      <div className="mt-2 text-xs font-bold text-emerald-600 flex items-center justify-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5" />
                        All players have finished voting!
                      </div>
                    );
                  }
                  return (
                    <div className="mt-2 text-xs font-semibold text-gray-500 flex items-center justify-center gap-1">
                      <span>Still voting:</span>
                      <span className="text-purple-700 font-bold">
                        {pending.map(p => p.name).join(', ')}
                      </span>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <div className="text-center text-xs font-bold text-gray-500">
                Waiting for the host to continue to the next round...
              </div>
            )}
          </div>

          {/* Host Missing Votes Gatekeeper Modal */}
          {showPendingVotesPrompt && (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-xs p-4 animate-in fade-in">
              <div className="bg-white rounded-3xl p-6 md:p-8 max-w-md w-full shadow-2xl border border-gray-100 animate-in zoom-in-95 text-center">
                <div className="w-14 h-14 rounded-2xl bg-amber-100 text-amber-700 flex items-center justify-center mx-auto mb-4">
                  <AlertCircle className="w-7 h-7" />
                </div>
                <h3 className="text-2xl font-black text-gray-900">
                  Players Still Voting
                </h3>
                <p className="text-gray-600 text-xs mt-1.5">
                  Some players haven&rsquo;t cast all their votes on other people&rsquo;s words:
                </p>

                <div className="my-4 bg-gray-50 rounded-2xl p-3 border border-gray-200 max-h-48 overflow-y-auto space-y-2 text-left">
                  {pendingVotersList.map(v => (
                    <div key={v.playerId} className="flex items-center justify-between text-xs p-2 bg-white rounded-xl border border-gray-100">
                      <span className="font-extrabold text-gray-800">👤 {v.name}</span>
                      <span className="text-amber-700 font-bold bg-amber-50 px-2 py-0.5 rounded-md border border-amber-200">
                        {v.pendingCount} vote{v.pendingCount > 1 ? 's' : ''} left
                      </span>
                    </div>
                  ))}
                </div>

                <p className="text-xs text-gray-500 font-medium mb-6">
                  Do you want to wait for these players to finish voting, or proceed anyway to the next round?
                </p>

                <div className="flex flex-col sm:flex-row items-center gap-2.5">
                  <button
                    onClick={() => setShowPendingVotesPrompt(false)}
                    className="w-full sm:flex-1 py-3 px-4 rounded-xl border border-gray-300 font-bold text-gray-700 hover:bg-gray-50 transition-colors text-xs"
                  >
                    Wait for Players
                  </button>
                  <button
                    onClick={() => {
                      setShowPendingVotesPrompt(false);
                      handleStartRound();
                    }}
                    className="w-full sm:flex-1 py-3 px-4 rounded-xl bg-gradient-to-r from-purple-600 to-indigo-600 text-white font-extrabold shadow-md hover:from-purple-700 hover:to-indigo-700 transition-all text-xs"
                  >
                    Proceed Anyway ➡️
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* GAME OVER PHASE */}
      {currentRoom && currentRoom.status === 'game_over' && (
        <div className="bg-white rounded-3xl p-8 md:p-12 shadow-xl border border-gray-100 text-center max-w-xl mx-auto animate-in zoom-in-95">
          <div className="w-20 h-20 bg-gradient-to-tr from-amber-400 to-yellow-500 text-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-xl shadow-amber-400/30">
            <Trophy className="w-10 h-10" />
          </div>
          <h2 className="text-3xl font-black text-gray-900">Game Over!</h2>
          <p className="text-gray-600 mt-1">Final Room Rankings:</p>

          <div className="my-6 space-y-3 text-left">
            {sortedPlayers.map((player, idx) => (
              <div
                key={player.id}
                className={`flex items-center justify-between p-4 rounded-2xl border ${
                  idx === 0
                    ? 'bg-amber-50 border-amber-300 ring-2 ring-amber-400'
                    : 'bg-gray-50 border-gray-200'
                }`}
              >
                <div className="flex items-center space-x-3">
                  <span
                    className={`w-7 h-7 rounded-full flex items-center justify-center font-black text-xs ${
                      idx === 0
                        ? 'bg-amber-400 text-amber-900'
                        : idx === 1
                        ? 'bg-gray-300 text-gray-800'
                        : idx === 2
                        ? 'bg-amber-700 text-white'
                        : 'bg-gray-200 text-gray-600'
                    }`}
                  >
                    #{idx + 1}
                  </span>
                  <div>
                    <span className="font-extrabold text-gray-900 text-base">{player.name}</span>
                    {idx === 0 && (
                      <span className="ml-2 text-xs font-bold text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">
                        🏆 Winner!
                      </span>
                    )}
                  </div>
                </div>
                <span className="text-xl font-black text-purple-900">{player.totalScore} pts</span>
              </div>
            ))}
          </div>

          {isHost ? (
            <button
              onClick={handleStartRound}
              className="px-8 py-3 bg-purple-600 hover:bg-purple-700 text-white font-bold rounded-xl shadow-lg transition-all"
            >
              Play Rematch
            </button>
          ) : (
            <p className="text-xs text-gray-500 font-medium">Host can start a rematch!</p>
          )}
        </div>
      )}

      {/* Word Details Popover */}
      {popoverWord && popoverPosition && (
        <WordPopover
          word={popoverWord}
          position={popoverPosition}
          onClose={() => {
            setPopoverWord(null);
            setPopoverPosition(null);
          }}
        />
      )}
    </div>
  );
};

export default StopGameMultiplayer;
